// src/app/invoices.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Keyboard,
  ScrollView,
  Platform,
} from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IoCalendarOutline } from 'react-icons/io5';
import { MdAdd, MdRemove } from 'react-icons/md';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

// --- small helpers for create invoice form ---
const fmtDate = (d) => d.toISOString().split('T')[0];         // YYYY-MM-DD
const fmtTime = (d) => d.toISOString().split('T')[1].slice(0,8); // HH:mm:ss

export default function Invoices() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Invoice list state
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);

  // Create invoice form state
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerName, setCustomerName] = useState('');

  const [title, setTitle] = useState('');
  const [jobId, setJobId] = useState(0);
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [workCompletedDate, setWorkCompletedDate] = useState(() => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [isNotesVisible, setIsNotesVisible] = useState(false);

  // Preview and email data for after form is cleared - now includes business info
  const [previewInvoiceData, setPreviewInvoiceData] = useState({});
  const [businessInfo, setBusinessInfo] = useState(null);
  const [lastCreatedInvoiceId, setLastCreatedInvoiceId] = useState(null);

  // derived input bindings for date/time fields - using local time like schedule.jsx
  const dateTimeStr = useMemo(() => {
    if (!(workCompletedDate instanceof Date) || isNaN(workCompletedDate)) return '';
    const pad = (n) => String(n).toString().padStart(2, '0');
    const yyyy = workCompletedDate.getFullYear();
    const mm = pad(workCompletedDate.getMonth() + 1);
    const dd = pad(workCompletedDate.getDate());
    const hh = pad(workCompletedDate.getHours());
    const mi = pad(workCompletedDate.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }, [workCompletedDate]);

  // inline customer addition
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    notes: '', // Add notes field to initial state
  });

  // Custom Modal Component for better-looking dialogs
  const [customModal, setCustomModal] = useState({
    open: false,
    title: '',
    message: '',
    buttons: []
  });

  // Email state for sending invoices
  const [sendingEmail, setSendingEmail] = useState(false);

  // Payment status state
  const [paymentStatus, setPaymentStatus] = useState('no');

  const showCustomModal = (title, message, buttons) => {
    setCustomModal({
      open: true,
      title,
      message,
      buttons
    });
  };

  const closeCustomModal = () => {
    setCustomModal({
      open: false,
      title: '',
      message: '',
      buttons: []
    });
  };

  const PAGE_SIZE = 25;

  useEffect(() => {
    const titleP = searchParams.get('title');
    const jobIdP = searchParams.get('jobId');
    const descP = searchParams.get('description');
    const priceP = searchParams.get('price');
    const selectedDate = searchParams.get('selectedDate');
    const selectedTime = searchParams.get('selectedTime');
    const customerNameP = searchParams.get('customerName');

    if (titleP) setTitle(titleP);
    if (jobIdP) setJobId(Number(jobIdP) || 0);
    if (descP) setDescription(descP);
    if (priceP) setPrice(String(priceP));
    if (customerNameP) {
      setCustomerName(customerNameP);
      setSearchQuery(customerNameP);
      setSelectedCustomer(customerNameP);
    }

    if (selectedDate && selectedTime) {
      const d = new Date(`${selectedDate}T${selectedTime}Z`);
      if (!isNaN(d.getTime())) setWorkCompletedDate(d);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchCustomersFromDB = async () => {
      try {
        const token = getToken();
        const { data } = await axios.get(`${API_BASE_URL}/customers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const list = Array.isArray(data) ? data : data?.customers ?? [];
        setCustomers(list);

        const cn = searchParams.get('customerName');
        if (cn && list.some((c) => c.name === cn)) {
          setFilteredCustomers([]);
        }
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
    };
    fetchCustomersFromDB();
  }, [searchParams]);

  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);

          if (parsedPermissions?.invoice_perm === 'none') {
            alert("You don't have permission to view or create invoices. Please contact your account administrator.");
            navigate('/');
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (!q.trim()) {
      setFilteredCustomers([]);
      return;
    }
    const t = q.toLowerCase();
    const matches = customers.filter((c) => (c?.name ?? '').toLowerCase().includes(t));
    setFilteredCustomers(matches);
  };

  const saveInvoice = async () => {
    // Ensure we have a customer name to work with
    const customerToFind = selectedCustomer || searchQuery || customerName || '';
    
    if (!customerToFind.trim()) {
      console.error('Error: No customer selected or entered.');
      showCustomModal(
        'Error',
        'Please select a valid customer from the list or add a new one.',
        [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
      );
      return;
    }

    const selectedCustomerData = customers.find(
      (c) => (c?.name ?? '').trim().toLowerCase() === customerToFind.trim().toLowerCase()
    );

    if (!selectedCustomerData) {
      console.error('Error: Selected customer not found.');
      showCustomModal(
        'Error',
        'Please select a valid customer from the list or add a new one.',
        [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
      );
      return;
    }

    // Ensure price defaults to "0.00" if blank or invalid
    const cleanedPrice = price && price.trim() !== '' ? price : '0.00';

    const newInvoice = {
      customer: selectedCustomerData.name,
      customerId: selectedCustomerData.id,
      title,
      description,
      price: cleanedPrice,
      selectedDate: fmtDate(workCompletedDate),
      selectedTime: fmtTime(workCompletedDate),
      jobId: jobId || 0,
      paid: paymentStatus, // Include payment status
      notes,
    };

    try {
      const token = getToken();
      console.log('🚀 Creating invoice with data:', newInvoice);

      const response = await axios.post(`${API_BASE_URL}/invoices`, newInvoice, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      console.log('✅ Invoice creation response:', response.data);

      const invoiceId = response.data?.id;
      const receivedBusinessInfo = response.data?.businessInfo;

      // Store business info from backend response
      if (receivedBusinessInfo) {
        setBusinessInfo(receivedBusinessInfo);
        console.log('📝 Business info received from backend:', receivedBusinessInfo);
      }

      if (!invoiceId) {
        console.error('❌ No invoice ID returned from backend');
        showCustomModal(
          'Warning',
          'Invoice was created but ID was not returned. Please try emailing from the invoices list.',
          [{ text: 'OK', style: 'primary', onPress: closeCustomModal }]
        );
        clearForm();
        refreshInvoicesList();
        return;
      }

      console.log('📧 Invoice created successfully with ID:', invoiceId);

      // Store invoice and customer data before clearing form
      const invoiceDataForPreview = {
        customer: selectedCustomerData.name,
        title,
        description,
        price: cleanedPrice,
        selectedDate: fmtDate(workCompletedDate),
        selectedTime: fmtTime(workCompletedDate),
        notes,
      };
      
      setPreviewInvoiceData(invoiceDataForPreview);
      setLastCreatedInvoiceId(invoiceId);

      // Clear the form and refresh invoices list first
      clearForm();
      refreshInvoicesList();

      // Email function that updates the modal to show "Sending..." state
      const emailInvoicePDF = async () => {
        if (sendingEmail) return;
        
        try {
          setSendingEmail(true);
          
          // Update the modal to show "Sending..." state
          showCustomModal(
            'Invoice Created Successfully! 🎉',
            'Your invoice has been created. What would you like to do next?',
            [
              {
                text: 'Sending…',
                style: 'primary',
                disabled: true,
                onPress: () => {} // Do nothing while sending
              },
              {
                text: '👁️ Preview Invoice',
                style: 'secondary',
                onPress: () => {
                  closeCustomModal();
                  setPreviewOpen(true);
                }
              },
              {
                text: '📋 View Invoices',
                style: 'default',
                onPress: closeCustomModal
              }
            ]
          );

          console.log(`📧 Attempting to email invoice ${invoiceId} to customer...`);
          
          const emailResponse = await axios.post(`${API_BASE_URL}/invoices/${invoiceId}/email`, {}, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });

          console.log('Email response:', emailResponse.data);
          
          if (emailResponse.data && emailResponse.data.success) {
            showCustomModal(
              'Email Sent Successfully! ✅',
              `Invoice has been emailed as PDF to ${selectedCustomerData.email || selectedCustomerData.name}`,
              [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
            );
          } else {
            showCustomModal(
              'Email Status Unclear ⚠️',
              emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
              [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
            );
          }
        } catch (emailError) {
          console.error('Error sending invoice email:', emailError);
          console.error('Email error response:', emailError.response?.data);
          
          let errorMessage = 'Failed to send email. Please try again.';
          
          if (emailError.response?.status === 400) {
            errorMessage = `Customer email issue: ${selectedCustomerData.email ? 'Email may be invalid' : 'No email address on file'}. Please update customer information.`;
          } else if (emailError.response?.status === 404) {
            errorMessage = 'Invoice not found. Please try creating the invoice again.';
          } else if (emailError.response?.data?.detail) {
            errorMessage = emailError.response.data.detail;
          } else if (emailError.response?.data?.message) {
            errorMessage = emailError.response.data.message;
          } else if (emailError.message) {
            errorMessage = `Network error: ${emailError.message}`;
          }
          
          showCustomModal(
            'Email Failed ❌',
            errorMessage,
            [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
          );
        } finally {
          setSendingEmail(false);
        }
      };

      // Show beautiful three-option modal matching mobile version
      showCustomModal(
        'Invoice Created Successfully! 🎉',
        'Your invoice has been created. What would you like to do next?',
        [
          {
            text: '📧 Email PDF to Customer',
            style: 'primary',
            onPress: emailInvoicePDF
          },
          {
            text: '👁️ Preview Invoice',
            style: 'secondary',
            onPress: () => {
              closeCustomModal();
              setPreviewOpen(true);
            }
          },
          {
            text: '📋 View Invoices',
            style: 'default',
            onPress: closeCustomModal
          }
        ]
      );
      
    } catch (error) {
      console.error('Error saving invoice:', error);
      console.error('Invoice creation error response:', error.response?.data);
      
      let errorMessage = 'Failed to save invoice.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid invoice data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to create invoices.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showCustomModal(
        'Error',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  // Helper function to clear the form
  const clearForm = () => {
    setTitle('');
    setDescription('');
    setPrice('');
    setNotes('');
    setSearchQuery('');
    setSelectedCustomer('');
    setCustomerName('');
    setFilteredCustomers([]);
    setIsNotesVisible(false);
    setAddingCustomer(false);
    setWorkCompletedDate(() => {
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      return d;
    });
    setNewCustomer({
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      notes: '', // Reset notes field
    });
  };

  // Helper function to refresh invoices list
  const refreshInvoicesList = () => {
    setInvoices([]);
    setHasMore(true);
    setPage(1);
    loadInvoices(1);
  };

  const handleAddCustomer = async (newCustomer) => {
    try {
      const token = getToken();
      console.log('🚀 Creating customer with data:', newCustomer);
      
      // Ensure notes field is included (even if empty)
      const customerPayload = {
        ...newCustomer,
        notes: newCustomer.notes || '', // Add notes field
      };
      
      const { data } = await axios.post(`${API_BASE_URL}/customers/returnId`, customerPayload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      
      console.log('✅ Customer creation response:', data);
      
      // The API response might not have the name field, so use the original newCustomer.name
      const addedCustomer = {
        ...data,
        name: data.name || newCustomer.name, // Fallback to original name if API doesn't return it
        id: data.id || data._id || Date.now() // Handle different possible ID fields
      };
      
      // Use functional update to ensure we have the latest state
      setCustomers(prevCustomers => {
        const updatedCustomers = [...prevCustomers, addedCustomer];
        console.log('📝 Updated customers array:', updatedCustomers.map(c => c.name));
        return updatedCustomers;
      });

      // Set the customer as selected - use a timeout to ensure state is updated
      setTimeout(() => {
        setCustomerName(addedCustomer.name);
        setSearchQuery(addedCustomer.name);
        setSelectedCustomer(addedCustomer.name);
        setFilteredCustomers([]);
        setAddingCustomer(false);
        
        console.log('✅ Customer selected:', addedCustomer.name);
      }, 100);
      
      // Reset the newCustomer form including notes
      setNewCustomer({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: '',
        notes: '', // Reset notes field
        showNotes: false, // Reset show notes flag
      });
      
      showCustomModal(
        'Customer Added Successfully! ✅',
        `${addedCustomer.name} has been added to your customer list and is now selected.`,
        [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
      );
    } catch (error) {
      console.error('Error adding customer:', error);
      console.error('Customer creation error response:', error.response?.data);
      
      let errorMessage = 'Failed to add customer. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid customer data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to create customers.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Invalid customer data format. Please check all required fields are filled.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showCustomModal(
        'Error',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    }
  };

  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  const hasInvoiceWritePermissions = () => {
    return userPermissions?.invoice_perm === 'write';
  };

  const loadInvoices = async (nextPage = 1) => {
    if (loading || !hasMore) return;
    try {
      setLoading(true);
      const token = getToken();
      const { data } = await axios.get(
        `${API_BASE_URL}/invoices?page=${nextPage}&page_size=${PAGE_SIZE}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      const list = Array.isArray(data) ? data : data?.invoices ?? [];

      setInvoices((prev) => (nextPage === 1 ? list : [...prev, ...list]));
      setHasMore(list.length >= PAGE_SIZE);
      setPage(nextPage);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      alert('Failed to load invoices.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setInvoices([]);
    setHasMore(true);
    setPage(1);
    loadInvoices(1);
  }, []);

  const formatDate = (selectedDate, selectedTime) => {
    if (!selectedDate || !selectedTime) return 'Invalid Date';
    const d = new Date(`${selectedDate}T${selectedTime}Z`);
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const renderItem = ({ item }) => {
    if (!item || typeof item !== 'object') return null;

    const when = formatDate(item.selectedDate, item.selectedTime);
    const idText = `#${item.id ?? 'N/A'}`;

    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigate(`/invoice-details?id=${encodeURIComponent(item.id)}`)}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.customer}>{item.customer || 'Unknown Customer'}</Text>
          <View style={styles.dateContainer}>
            <Text style={styles.dateText}>{idText}</Text>
            <IoCalendarOutline size={16} color="green" style={{ marginHorizontal: 8 }} aria-hidden />
            <Text style={styles.dateText}>{when}</Text>
          </View>
        </View>

        <Text style={styles.title}>{item.title || 'No Title'}</Text>
        {canSeePrices() && item.price && (
          <Text style={styles.price}>{`$${item.price}`}</Text>
        )}

        <Text style={styles.invoiceDetail}>
          {`Work Completed: ${when}`}
        </Text>

        <Text style={styles.invoiceDetail}>
          Sent:{' '}
          {item.sentEmail === 'yes' && item.sentText === 'yes'
            ? 'Email, Text'
            : item.sentEmail === 'yes'
            ? 'Email'
            : item.sentText === 'yes'
            ? 'Text'
            : 'No'}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.mainContainer}>
      {/* Left side - Invoice list */}
      <View style={styles.leftPanel}>
        <Text style={styles.header}>All Invoices</Text>

        {invoices.length === 0 && !loading ? (
          <Text style={styles.noInvoicesText}>There are no invoices to display.</Text>
        ) : (
          <FlatList
            data={invoices}
            keyExtractor={(item, index) => String(item?.id ?? index)}
            renderItem={renderItem}
            ListFooterComponent={
              hasMore ? (
                <TouchableOpacity
                  style={styles.loadMoreButton}
                  onPress={() => loadInvoices(page + 1)}
                  disabled={loading}
                >
                  <Text style={styles.loadMoreText}>{loading ? 'Loading…' : 'Load More'}</Text>
                </TouchableOpacity>
              ) : null
            }
          />
        )}
      </View>

      {/* Right side - Create invoice form */}
      {hasInvoiceWritePermissions() ? (
        <View style={styles.rightPanel}>
          <ScrollView 
            contentContainerStyle={styles.formContainer} 
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={true}
          >
            <Text style={styles.formHeader}>Create New Invoice</Text>
            
            {/* Customer selection */}
            <Text style={styles.label}>Customer Name</Text>
            <TextInput
              style={styles.input}
              value={searchQuery}
              onChangeText={handleSearch}
              onBlur={() => {
                if (customers.some((c) => c.name === searchQuery)) setFilteredCustomers([]);
              }}
              placeholder="Search or add customer"
              autoComplete="off"
              autoCorrect={false}
            />

            {searchQuery ? (
              <View style={styles.searchResults}>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer, i) => (
                    <TouchableOpacity
                      key={customer.id || `s-${i}`}
                      onPress={() => {
                        setCustomerName(customer.name);
                        setSearchQuery(customer.name);
                        setSelectedCustomer(customer.name);
                        setFilteredCustomers([]);
                      }}
                    >
                      <Text style={styles.searchResult}>{customer.name}</Text>
                    </TouchableOpacity>
                  ))
                ) : !customers.some((c) => c.name === searchQuery) ? (
                  <View style={styles.addCustomerRow}>
                    <Text style={styles.searchResult}>{searchQuery}</Text>
                    <TouchableOpacity
                      style={styles.addCustomerButton}
                      onPress={() => {
                        setNewCustomer((nc) => ({ ...nc, name: searchQuery }));
                        setAddingCustomer(true);
                      }}
                    >
                      <MdAdd size={20} color="green" />
                      <Text style={styles.addCustomerText}>Add Customer</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
            ) : null}

            {/* Inline Add Customer */}
            {addingCustomer && (
              <View style={styles.inlineModal}>
                <Text style={styles.modalHeader}>Add Customer</Text>
                {[
                  ['Customer Name', 'name'],
                  ['Address', 'address'],
                  ['City', 'city'],
                  ['State', 'state'],
                  ['Zip', 'zip'],
                  ['Phone', 'phone'],
                  ['Email', 'email'],
                ].map(([label, key]) => (
                  <View key={key} style={{ marginBottom: 10 }}>
                    <Text style={styles.modalLabel}>{label}</Text>
                    <TextInput
                      style={styles.input}
                      value={newCustomer[key]}
                      onChangeText={(t) => setNewCustomer((nc) => ({ ...nc, [key]: t }))}
                      placeholder={label}
                      autoComplete="off"
                      autoCorrect={false}
                    />
                  </View>
                ))}

                {!newCustomer.showNotes ? (
                  <TouchableOpacity
                    style={styles.addNotesButton}
                    onPress={() => setNewCustomer((nc) => ({ ...nc, showNotes: true }))}
                  >
                    <MdAdd size={20} color="green" />
                    <Text style={styles.addNotesText}>Add Notes</Text>
                  </TouchableOpacity>
                ) : (
                  <>
                    <TouchableOpacity
                      style={styles.hideNotesButton}
                      onPress={() => setNewCustomer((nc) => ({ ...nc, showNotes: false, notes: '' }))}
                    >
                      <MdRemove size={20} color="red" />
                      <Text style={styles.hideNotesText}>Hide Notes</Text>
                    </TouchableOpacity>
                    <View style={{ marginBottom: 10 }}>
                      <Text style={styles.modalLabel}>Notes (for internal use only):</Text>
                      <TextInput
                        style={[styles.input, styles.textArea]}
                        value={newCustomer.notes}
                        onChangeText={(t) => setNewCustomer((nc) => ({ ...nc, notes: t }))}
                        placeholder="Enter notes here..."
                        multiline
                        numberOfLines={4}
                        autoComplete="off"
                        autoCorrect={false}
                      />
                    </View>
                  </>
                )}

                <TouchableOpacity style={styles.saveButton} onPress={() => handleAddCustomer(newCustomer)}>
                  <Text style={styles.saveButtonText}>Save Customer</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.returnButton} onPress={() => setAddingCustomer(false)}>
                  <Text style={styles.returnButtonText}>Return To Invoice</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Core invoice fields */}
            <Text style={styles.label}>Title</Text>
            <TextInput 
              style={styles.input} 
              value={title} 
              onChangeText={setTitle}
              autoComplete="off"
              autoCorrect={false}
            />

            <Text style={styles.label}>Description</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={description}
              onChangeText={setDescription}
              multiline
              autoComplete="off"
              autoCorrect={false}
            />

            <Text style={styles.label}>Price</Text>
            <TextInput
              style={styles.input}
              value={price}
              onChangeText={(text) => {
                // Allow user to type currency formats but clean it for storage
                const cleanedPrice = text.replace(/[^0-9.]/g, ''); // Remove everything except numbers and decimal points
                setPrice(cleanedPrice);
              }}
              inputMode="decimal"
              keyboardType="numeric"
              placeholder="0.00"
              autoComplete="off"
              autoCorrect={false}
            />

            <Text style={styles.label}>Work Completed</Text>
            <View style={styles.dateTimeContainer}>
              <Text style={styles.dateTimePreview}>
                {workCompletedDate ? workCompletedDate.toLocaleString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                }) : 'Select date and time'}
              </Text>
              <input
                type="datetime-local"
                value={dateTimeStr}
                onChange={(e) => {
                  const next = new Date(e.target.value);
                  if (!isNaN(next)) {
                    next.setSeconds(0, 0);
                    setWorkCompletedDate(next);
                  }
                }}
                style={htmlDateTimeInputStyle}
              />
            </View>

            <Text style={styles.label}>Payment Status</Text>
            <View style={styles.paymentContainer}>
              <TouchableOpacity
                style={styles.paymentOption}
                onPress={() => setPaymentStatus('no')}
              >
                <View style={[styles.checkbox, paymentStatus === 'no' && styles.checkboxSelected]}>
                  {paymentStatus === 'no' && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.paymentOptionText}>Not Paid</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.paymentOption}
                onPress={() => setPaymentStatus('yes')}
              >
                <View style={[styles.checkbox, paymentStatus === 'yes' && styles.checkboxSelected]}>
                  {paymentStatus === 'yes' && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.paymentOptionText}>Paid in Full</Text>
              </TouchableOpacity>
            </View>

            {!isNotesVisible ? (
              <TouchableOpacity style={styles.addNotesButton} onPress={() => setIsNotesVisible(true)}>
                <MdAdd size={20} color="green" />
                <Text style={styles.addNotesText}>Add Notes</Text>
              </TouchableOpacity>
            ) : (
              <>
                <TouchableOpacity style={styles.hideNotesButton} onPress={() => setIsNotesVisible(false)}>
                  <MdRemove size={20} color="red" />
                  <Text style={styles.hideNotesText}>Hide Notes</Text>
                </TouchableOpacity>
                <View style={styles.fieldContainer}>
                  <Text style={styles.label}>Notes (for internal use only):</Text>
                  <TextInput
                    style={[styles.input, styles.textArea]}
                    placeholder="Enter notes here..."
                    multiline
                    numberOfLines={4}
                    onChangeText={setNotes}
                    value={notes}
                    autoComplete="off"
                    autoCorrect={false}
                  />
                </View>
              </>
            )}

            <TouchableOpacity style={styles.saveButton} onPress={saveInvoice}>
              <Text style={styles.saveButtonText}>Save Invoice</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.clearButton} onPress={() => {
              // Clear all form fields
              setTitle('');
              setDescription('');
              setPrice('');
              setNotes('');
              setSearchQuery('');
              setSelectedCustomer('');
              setCustomerName('');
              setFilteredCustomers([]);
              setIsNotesVisible(false);
              setAddingCustomer(false);
              setWorkCompletedDate(() => {
                const d = new Date();
                d.setHours(12, 0, 0, 0);
                return d;
              });
              setNewCustomer({
                name: '',
                address: '',
                city: '',
                state: '',
                zip: '',
                phone: '',
                email: '',
                notes: '', // Include notes in reset
              });
            }}>
              <Text style={styles.clearButtonText}>Clear Form</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      ) : (
        <View style={styles.rightPanel}>
          <Text style={styles.readOnlyText}>You have read-only access to invoices.</Text>
        </View>
      )}

      {/* Preview overlay */}
      {previewOpen && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <iframe
              srcDoc={`
                <!DOCTYPE html>
                <html>
                <head>
                  <meta charset="UTF-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>Invoice INV-${lastCreatedInvoiceId || 'XXXX'} - ${previewInvoiceData.customer || 'Customer'}</title>
                  <style>
                    @media print {
                      body { margin: 0; }
                      .action-buttons { display: none; }
                    }
                    
                    body {
                      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                      line-height: 1.6;
                      color: #333;
                      max-width: 800px;
                      margin: 0 auto;
                      padding: 60px 30px 100px 30px;
                      background: #fff;
                    }
                    
                    .header {
                      display: flex;
                      justify-content: space-between;
                      align-items: flex-start;
                      margin-bottom: 40px;
                      padding-bottom: 20px;
                      border-bottom: 3px solid #dc2626;
                      margin-top: 80px;
                    }
                    
                    .company-info {
                      flex: 1;
                    }
                    
                    .company-name {
                      font-size: 28px;
                      font-weight: bold;
                      color: #dc2626;
                      margin-bottom: 10px;
                    }
                    
                    .company-details {
                      color: #666;
                      font-size: 14px;
                    }
                    
                    .invoice-info {
                      text-align: right;
                      flex: 1;
                    }
                    
                    .invoice-title {
                      font-size: 32px;
                      font-weight: bold;
                      color: #dc2626;
                      margin-bottom: 10px;
                    }
                    
                    .invoice-details {
                      font-size: 14px;
                      color: #666;
                    }
                    
                    .customer-section {
                      margin-bottom: 30px;
                    }
                    
                    .section-title {
                      font-size: 18px;
                      font-weight: bold;
                      color: #374151;
                      margin-bottom: 15px;
                      padding-bottom: 5px;
                      border-bottom: 2px solid #e5e7eb;
                    }
                    
                    .customer-info {
                      background: #fef2f2;
                      padding: 20px;
                      border-radius: 8px;
                      border-left: 4px solid #dc2626;
                    }
                    
                    .customer-name {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 10px;
                    }
                    
                    .services-section {
                      margin-bottom: 30px;
                    }
                    
                    .service-item {
                      background: #fff;
                      border: 1px solid #e5e7eb;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 20px;
                      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    }
                    
                    .service-title {
                      font-size: 20px;
                      font-weight: bold;
                      color: #1f2937;
                      margin-bottom: 15px;
                    }
                    
                    .service-description {
                      color: #4b5563;
                      margin-bottom: 15px;
                      line-height: 1.7;
                      white-space: pre-wrap;
                    }
                    
                    .service-date {
                      color: #6b7280;
                      font-size: 14px;
                      font-style: italic;
                    }
                    
                    .pricing-section {
                      background: #fef2f2;
                      border-radius: 8px;
                      padding: 25px;
                      margin-bottom: 30px;
                      border: 2px solid #dc2626;
                    }
                    
                    .price-row {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      padding: 15px 0;
                      border-bottom: 1px solid #fecaca;
                    }
                    
                    .price-row:last-child {
                      border-bottom: none;
                      font-size: 24px;
                      font-weight: bold;
                      color: #dc2626;
                      margin-top: 10px;
                      padding-top: 20px;
                      border-top: 2px solid #dc2626;
                    }
                    
                    .price-label {
                      font-size: 18px;
                      color: #374151;
                    }
                    
                    .price-amount {
                      font-size: 18px;
                      font-weight: bold;
                      color: #1f2937;
                    }
                    
                    .total-label {
                      font-size: 20px;
                      font-weight: bold;
                    }
                    
                    .total-amount {
                      font-size: 24px;
                      font-weight: bold;
                      color: #dc2626;
                    }
                    
                    .payment-section {
                      margin-top: 40px;
                      padding: 20px;
                      background: #f0fdf4;
                      border-radius: 8px;
                      border-left: 4px solid #22c55e;
                    }
                    
                    .payment-title {
                      font-size: 16px;
                      font-weight: bold;
                      color: #166534;
                      margin-bottom: 10px;
                    }
                    
                    .payment-text {
                      font-size: 14px;
                      color: #15803d;
                      line-height: 1.6;
                    }
                    
                    .footer {
                      margin-top: 50px;
                      padding-top: 20px;
                      border-top: 2px solid #e5e7eb;
                      text-align: center;
                      color: #6b7280;
                      font-size: 14px;
                    }
                    
                    .action-buttons {
                      position: fixed;
                      top: 20px;
                      right: 20px;
                      display: flex;
                      flex-direction: row;
                      gap: 10px;
                      z-index: 1000;
                      background: rgba(255,255,255,0.95);
                      padding: 10px;
                      border-radius: 10px;
                      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    }
                    
                    .action-button {
                      background: #dc2626;
                      color: white;
                      border: none;
                      padding: 12px 20px;
                      border-radius: 8px;
                      font-size: 14px;
                      font-weight: bold;
                      cursor: pointer;
                      box-shadow: 0 2px 4px rgba(220, 38, 38, 0.3);
                      transition: all 0.2s;
                    }
                    
                    .action-button:hover {
                      background: #b91c1c;
                      transform: translateY(-2px);
                      box-shadow: 0 4px 8px rgba(220, 38, 38, 0.4);
                    }
                    
                    .save-pdf-button {
                      background: #059669;
                    }
                    
                    .save-pdf-button:hover {
                      background: #047857;
                    }
                  </style>
                </head>
                <body>
                  <div class="header">
                    <div class="company-info">
                      <div class="company-name">${businessInfo?.businessName || 'Your Business Name'}</div>
                      <div class="company-details">
                        ${(() => {
                          if (!businessInfo) {
                            return `123 Business Street<br>City, State 12345<br>Phone: (555) 123-4567<br>Email: info@yourbusiness.com`;
                          }
                          
                          const addressParts = [];
                          
                          if (businessInfo.address) addressParts.push(businessInfo.address);
                          if (businessInfo.city && businessInfo.state) {
                            const cityState = businessInfo.zip ? `${businessInfo.city}, ${businessInfo.state} ${businessInfo.zip}` : `${businessInfo.city}, ${businessInfo.state}`;
                            addressParts.push(cityState);
                          }
                          if (businessInfo.phone) {
                            addressParts.push(`Phone: ${businessInfo.phone}`);
                          }
                          if (businessInfo.email) {
                            addressParts.push(`Email: ${businessInfo.email}`);
                          }
                          
                          return addressParts.join('<br>') || 'Contact information not available';
                        })()}
                      </div>
                    </div>
                    <div class="invoice-info">
                      <div class="invoice-title">INVOICE</div>
                      <div class="invoice-details">
                        <strong>Invoice #:</strong> INV-${lastCreatedInvoiceId || 'XXXX'}<br>
                        <strong>Date:</strong> ${new Date().toLocaleDateString()}<br>
                        <strong>Due Date:</strong> ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div class="customer-section">
                    <div class="section-title">Invoice To:</div>
                    <div class="customer-info">
                      <div class="customer-name">${previewInvoiceData.customer || 'Customer Name'}</div>
                      ${(() => {
                        const customer = customers.find(c => c.name === previewInvoiceData.customer);
                        if (customer) {
                          return `
                            ${customer.address ? `<div>${customer.address}</div>` : ''}
                            ${customer.city && customer.state ? `<div>${customer.city}, ${customer.state} ${customer.zip || ''}</div>` : ''}
                            ${customer.phone ? `<div>Phone: ${customer.phone}</div>` : ''}
                            ${customer.email ? `<div>Email: ${customer.email}</div>` : ''}
                          `;
                        }
                        return '';
                      })()}
                    </div>
                  </div>

                  <div class="services-section">
                    <div class="section-title">Services Provided</div>
                    <div class="service-item">
                      <div class="service-title">${previewInvoiceData.title || 'Service Title'}</div>
                      <div class="service-description">${previewInvoiceData.description || 'Service description'}</div>
                      <div class="service-date">Work completed on: ${(() => {
                        if (previewInvoiceData.selectedDate && previewInvoiceData.selectedTime) {
                          const date = new Date(previewInvoiceData.selectedDate + 'T' + previewInvoiceData.selectedTime);
                          const formattedDate = date.toLocaleDateString();
                          const formattedTime = new Date('1970-01-01T' + previewInvoiceData.selectedTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                          return `${formattedDate} at ${formattedTime}`;
                        }
                        return 'Date not specified';
                      })()}</div>
                    </div>
                  </div>

                  <div class="pricing-section">
                    <div class="price-row">
                      <span class="price-label">Service Total:</span>
                      <span class="price-amount">$${parseFloat(previewInvoiceData.price || 0).toFixed(2)}</span>
                    </div>
                    <div class="price-row">
                      <span class="total-label">Amount Due:</span>
                      <span class="total-amount">$${parseFloat(previewInvoiceData.price || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  <div class="payment-section">
                    <div class="payment-title">Payment Information</div>
                    <div class="payment-text">
                      • Payment is due within 30 days of invoice date<br>
                      • Please include invoice number with payment<br>
                      • Late payments may be subject to additional fees<br>
                      • Contact us with any questions about this invoice
                    </div>
                  </div>

                  <div class="footer">
                    <p>Thank you for your business!</p>
                    <p><strong>Questions?</strong> Contact us at ${businessInfo?.phone || '(555) 123-4567'} or ${businessInfo?.email || 'info@yourbusiness.com'}</p>
                  </div>

                  <div class="action-buttons">
                    <button class="action-button save-pdf-button" onclick="window.print()">
                      📄 Save as PDF
                    </button>
                  </div>
                </body>
                </html>
              `}
              style={{
                width: '100%',
                height: '80vh',
                border: 'none',
                borderRadius: '8px'
              }}
              title="Invoice Preview"
            />
            
            <View style={styles.buttonGroup}>
              <TouchableOpacity 
                style={[styles.actionButton, styles.emailButton, sendingEmail && { opacity: 0.7 }]}
                onPress={async () => {
                  if (!lastCreatedInvoiceId) {
                    showCustomModal(
                      'Error',
                      'Invoice ID not available. Please try emailing from the invoices list.',
                      [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                    );
                    return;
                  }

                  const customerData = customers.find(
                    (c) => (c?.name ?? '').trim().toLowerCase() === (previewInvoiceData.customer || '').trim().toLowerCase()
                  );
                  
                  if (!customerData?.email) {
                    showCustomModal(
                      'Email Not Available ⚠️',
                      'Customer email not found. Please update customer information.',
                      [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                    );
                    return;
                  }

                  setPreviewOpen(false);
                  
                  try {
                    setSendingEmail(true);
                    
                    showCustomModal(
                      'Sending Email...',
                      'Please wait while we send your invoice.',
                      [
                        {
                          text: 'Sending…',
                          style: 'primary',
                          disabled: true,
                          onPress: () => {}
                        }
                      ]
                    );

                    const token = getToken();
                    const emailResponse = await axios.post(`${API_BASE_URL}/invoices/${lastCreatedInvoiceId}/email`, {}, {
                      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    });

                    if (emailResponse.data && emailResponse.data.success) {
                      showCustomModal(
                        'Email Sent Successfully! ✅',
                        `Invoice has been emailed as PDF to ${customerData.email}`,
                        [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
                      );
                    } else {
                      showCustomModal(
                        'Email Status Unclear ⚠️',
                        emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
                        [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                      );
                    }
                  } catch (emailError) {
                    console.error('Error sending invoice email:', emailError);
                    
                    let errorMessage = 'Failed to send email. Please try again.';
                    if (emailError.response?.status === 400) {
                      errorMessage = `Customer email issue. Please update customer information.`;
                    } else if (emailError.response?.data?.detail) {
                      errorMessage = emailError.response.data.detail;
                    }
                    
                    showCustomModal(
                      'Email Failed ❌',
                      errorMessage,
                      [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                    );
                  } finally {
                    setSendingEmail(false);
                  }
                }}
                disabled={sendingEmail}
              >
                <Text style={styles.emailButtonText}>
                  {sendingEmail ? 'Sending…' : '📧 Send to Customer'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity style={[styles.saveButton, { marginTop: 16 }]} onPress={() => {
                setPreviewOpen(false);
                // Show the three-option modal again when closing preview
                showCustomModal(
                  'Invoice Created Successfully! 🎉',
                  'Your invoice has been created. What would you like to do next?',
                  [
                    {
                      text: '📧 Email PDF to Customer',
                      style: 'primary',
                      onPress: async () => {
                        if (!lastCreatedInvoiceId) {
                          showCustomModal(
                            'Error',
                            'Invoice ID not available. Please try emailing from the invoices list.',
                            [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                          );
                          return;
                        }

                        const customerData = customers.find(
                          (c) => (c?.name ?? '').trim().toLowerCase() === (previewInvoiceData.customer || '').trim().toLowerCase()
                        );
                        
                        if (!customerData?.email) {
                          showCustomModal(
                            'Email Not Available ⚠️',
                            'Customer email not found. Please update customer information.',
                            [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                          );
                          return;
                        }

                        try {
                          setSendingEmail(true);
                          
                          showCustomModal(
                            'Sending Email...',
                            'Please wait while we send your invoice.',
                            [
                              {
                                text: 'Sending…',
                                style: 'primary',
                                disabled: true,
                                onPress: () => {}
                              }
                            ]
                          );

                          const token = getToken();
                          const emailResponse = await axios.post(`${API_BASE_URL}/invoices/${lastCreatedInvoiceId}/email`, {}, {
                            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                          });

                          if (emailResponse.data && emailResponse.data.success) {
                            showCustomModal(
                              'Email Sent Successfully! ✅',
                              `Invoice has been emailed as PDF to ${customerData.email}`,
                              [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
                            );
                          } else {
                            showCustomModal(
                              'Email Status Unclear ⚠️',
                              emailResponse.data?.message || 'Email may have been sent, but status is unclear.',
                              [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
                            );
                          }
                        } catch (emailError) {
                          console.error('Error sending invoice email:', emailError);
                          
                          let errorMessage = 'Failed to send email. Please try again.';
                          if (emailError.response?.status === 400) {
                            errorMessage = `Customer email issue. Please update customer information.`;
                          } else if (emailError.response?.data?.detail) {
                            errorMessage = emailError.response.data.detail;
                          }
                          
                          showCustomModal(
                            'Email Failed ❌',
                            errorMessage,
                            [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
                          );
                        } finally {
                          setSendingEmail(false);
                        }
                      }
                    },
                    {
                      text: '👁️ Preview Invoice',
                      style: 'secondary',
                      onPress: () => {
                        closeCustomModal();
                        setPreviewOpen(true);
                      }
                    },
                    {
                      text: '📋 View Invoices',
                      style: 'default',
                      onPress: closeCustomModal
                    }
                  ]
                );
              }}>
                <Text style={styles.saveButtonText}>Close Preview</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Custom Modal */}
      {customModal.open && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <Text style={styles.previewTitle}>{customModal.title}</Text>
            <Text>{customModal.message}</Text>
            <View style={styles.buttonGroup}>
              {customModal.buttons.map((button, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.actionButton,
                    button.style === 'primary' && { backgroundColor: 'blue' },
                    button.style === 'secondary' && { backgroundColor: 'gray' },
                    button.style === 'danger' && { backgroundColor: 'red' },
                    button.style === 'default' && { backgroundColor: '#f0f0f0' },
                  ]}
                  onPress={button.onPress}
                >
                  <Text style={[
                    styles.actionButtonText,
                    button.style === 'default' && { color: 'black' }
                  ]}>
                    {button.text}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: { 
    flex: 1, 
    flexDirection: 'row', 
    height: '100vh', 
    backgroundColor: '#fff' 
  },
  leftPanel: { 
    flex: 1, 
    padding: 20, 
    paddingBottom: 50, 
    backgroundColor: '#fff',
    borderRightWidth: 1,
    borderRightColor: '#e5e5e5'
  },
  rightPanel: { 
    flex: 1, 
    backgroundColor: '#f9f9f9',
    padding: 20
  },
  formContainer: { 
    paddingBottom: 50 
  },
  formHeader: {
    fontSize: 20, 
    fontWeight: 'bold', 
    marginBottom: 20, 
    textAlign: 'center',
    color: '#333'
  },
  header: {
    fontSize: 24, fontWeight: 'bold', marginBottom: 20, textAlign: 'center',
    textDecorationLine: 'underline',
  },

  // Invoice list styles
  card: {
    backgroundColor: '#f0f0f0',
    padding: 15,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 8,
    marginBottom: 15,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  customer: { fontSize: 16, fontWeight: 'bold', marginBottom: 5 },
  title: { fontSize: 14, marginBottom: 5 },
  price: { fontSize: 14, color: '#007BFF' },
  invoiceDetail: { fontSize: 12, color: '#555', marginTop: 5 },

  dateContainer: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  dateText: { fontSize: 14, color: 'green', fontWeight: 'bold', marginHorizontal: 0 },

  noInvoicesText: { textAlign: 'center', fontSize: 16, color: '#777', marginTop: 50 },

  loadMoreButton: {
    padding: 10, backgroundColor: '#007BFF', borderRadius: 5, alignItems: 'center', marginVertical: 10,
  },
  loadMoreText: { color: '#fff', fontSize: 16 },

  // Create invoice form styles
  label: { marginTop: 15, fontWeight: 'bold' },
  modalLabel: { fontWeight: 'bold', marginBottom: 5 },
  input: { borderWidth: 1, borderColor: '#ccc', padding: 8, borderRadius: 4 },
  textArea: { height: 100, textAlignVertical: 'top' },
  row: { flexDirection: 'row', gap: 10, marginTop: 10 },
  flex1: { flex: 1 },

  // Date/Time picker styles
  dateTimeContainer: { marginTop: 10 },
  dateTimePreview: { 
    fontSize: 14, 
    color: '#333', 
    marginBottom: 6,
    fontWeight: 'bold'
  },

  searchResults: { marginTop: 5, backgroundColor: '#f9f9f9', borderRadius: 4, padding: 10 },
  searchResult: { paddingVertical: 5, fontSize: 14 },
  addCustomerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  addCustomerButton: { flexDirection: 'row', alignItems: 'center', marginLeft: 10 },
  addCustomerText: { color: 'green', marginLeft: 5 },

  saveButton: { marginTop: 20, backgroundColor: 'blue', padding: 12, borderRadius: 6, alignItems: 'center' },
  saveButtonText: { color: 'white', fontWeight: 'bold' },
  clearButton: { marginTop: 10, backgroundColor: 'red', padding: 12, borderRadius: 6, alignItems: 'center' },
  clearButtonText: { color: 'white', fontWeight: 'bold' },
  returnButton: { marginTop: 10, backgroundColor: '#666', padding: 12, borderRadius: 6, alignItems: 'center' },
  returnButtonText: { color: 'white', fontWeight: 'bold' },

  fieldContainer: { marginTop: 0 },
  addNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 10 },
  addNotesText: { color: 'green', marginLeft: 5 },
  hideNotesButton: { flexDirection: 'row', alignItems: 'center', marginTop: 10 },
  hideNotesText: { color: 'red', marginLeft: 5 },

  inlineModal: {
    marginTop: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  modalHeader: { fontSize: 18, fontWeight: 'bold', marginBottom: 15 },

  readOnlyText: { textAlign: 'center', fontSize: 16, color: '#777', marginTop: 50 },

  // preview overlay
  previewOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1000,
  },
  previewCard: {
    width: 'min(640px, 100%)',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  previewTitle: { fontSize: 20, fontWeight: '700', marginBottom: 12 },
  bold: { fontWeight: '700' },
  buttonGroup: { marginTop: 16 },
  actionButton: {
    backgroundColor: '#f0f0f0',
    padding: 12,
    borderRadius: 6,
    alignItems: 'center',
    marginBottom: 10,
  },
  actionButtonText: { color: 'white', fontWeight: 'bold' },
  pdfButton: { backgroundColor: '#d3d3d3' },
  emailButton: { backgroundColor: '#d3d3d3' },
  pdfButtonText: { color: 'black', fontWeight: 'bold' },
  emailButtonText: { color: 'black', fontWeight: 'bold' },
  paymentContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 30,
    marginTop: 10,
  },
  paymentOption: { flexDirection: 'row', alignItems: 'center' },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: { backgroundColor: 'blue' },
  checkmark: { color: 'white', fontWeight: 'bold' },
  paymentOptionText: { fontSize: 14, color: '#333' },
});
const htmlDateTimeInputStyle = {
  padding: '8px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  marginRight: '10px',
  flex: 1,
};